import Image from "next/image";
import { useAnalytics } from "~/components/Analytics";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import { secondaryButtonStyle } from "~/landing-pages/components/CTA/cta-shared-styles";
import { ContentSectionBlocks } from "~/landing-pages/components/ContentSections/ContentSectionBlocks";
import { BaseContentSectionProps } from "~/landing-pages/components/ContentSections/content-section-shared-types";
import { blockStyle } from "~/landing-pages/components/ContentSections/shared-content-section-styles";
import {
  eyebrowColorStyle,
  primaryTextColorStyle,
  secondaryTextColorStyle,
} from "~/landing-pages/shared-landing-page-styles";
import { sanitizeRelativeUrl } from "~/lib/shared/sanitizeRelativeUrl";
import { Body, Headline } from "~/ui-library";

export type ContentSectionWithTestimonialProps = BaseContentSectionProps & {
  appearance: "brand" | "dark" | "light";
  blockIndex: number;
  primaryCTALink: string;
  primaryCTAText: string;
  quote?: {
    body: string;
    authorName: string;
    authorDetails: string;
  };
};

export const ContentSectionWithTestimonial: React.FC<
  ContentSectionWithTestimonialProps
> = ({
  blockIndex,
  appearance,
  imageUrl,
  imageAlt,
  eyebrow,
  headline,
  contentBlocks,
  primaryCTALink,
  primaryCTAText,
  quote,
}) => {
  const { analyticsEnhancedTrack } = useAnalytics();
  const analyticsVariables = {
    blockIndex,
    appearance,
  };
  const primaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Primary CTA", {
      ...analyticsVariables,
      linkText: primaryCTAText,
      linkUrl: primaryCTALink,
    });
  return (
    <div className={blockStyle({ appearance })}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto grid max-w-2xl grid-cols-1 items-start gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:gap-y-24">
          <div className="lg:pr-4">
            <div className="relative overflow-hidden rounded-3xl bg-gray-900 px-6 pb-12 pt-48 sm:px-12 sm:pt-36 lg:max-w-lg lg:px-8 lg:pb-8 lg:pt-64 xl:px-10 xl:pb-10">
              <Image
                alt={imageAlt}
                src={imageUrl}
                fill
                className="absolute inset-0 size-full object-cover brightness-125 saturate-0"
              />
              <div className="absolute inset-0 bg-gray-900 mix-blend-multiply" />
              <div
                className="absolute left-1/2 top-1/2 -ml-16 -translate-x-1/2 -translate-y-1/2 transform-gpu blur-3xl"
                aria-hidden="true"
              >
                <div
                  className="aspect-[1097/845] w-[68.5625rem] bg-gradient-to-tr from-[#ff4694] to-[#776fff] opacity-40"
                  style={{
                    clipPath:
                      "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
                  }}
                />
              </div>
              {quote ? (
                <figure
                  className="relative isolate"
                  key={`figure`}
                  data-testid="quote_content_block"
                >
                  <blockquote className="mb-4 font-semibold text-white lg:mb-12">
                    <Body
                      size={"large"}
                      className={secondaryTextColorStyle({
                        appearance: "dark",
                      })}
                      bold
                    >
                      {quote.body}
                    </Body>
                  </blockquote>
                  <figcaption className="text-gray-300">
                    <Body
                      as="span"
                      className={primaryTextColorStyle({
                        appearance: "dark",
                        className: "mb-1",
                      })}
                      bold
                    >
                      {quote.authorName},
                    </Body>
                    <Body
                      as="span"
                      size="small"
                      className={primaryTextColorStyle({
                        appearance: "dark",
                      })}
                    >
                      &nbsp;{quote.authorDetails}
                    </Body>
                  </figcaption>
                </figure>
              ) : null}
            </div>
          </div>
          <div className="lg:max-w-lg">
            <Headline
              as="div"
              level={6}
              className={eyebrowColorStyle({ appearance, className: "mb-2" })}
            >
              {eyebrow}
            </Headline>
            <Headline
              level={2}
              as="h2"
              className={primaryTextColorStyle({ appearance })}
            >
              {headline}
            </Headline>
            <ContentSectionBlocks
              appearance={appearance}
              contentBlocks={contentBlocks}
            />

            <div className="mt-10 flex">
              <CTALink
                href={sanitizeRelativeUrl(primaryCTALink)}
                onClick={primaryButtonClick}
                className={secondaryButtonStyle({ appearance })}
              >
                {primaryCTAText}
                <span aria-hidden="true" className="align-text-top">
                  →
                </span>
              </CTALink>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
